import type { Api } from "./client";

export interface AdminStats {
  accounts_active: number;
  accounts_deleted: number;
  accounts_2fa: number;
  devices_active: number;
  servers_paired: number;
  sessions_active: number;
  invites_open: number;
  events_1h: number;
  failed_logins_24h: number;
}

export interface AccountRow {
  id: string;
  email: string;
  display_name: string;
  role: string;
  acct_kind: string;
  totp_enabled_at: string | null;
  locked_until: string | null;
  failed_login_count: number;
  created_at: string;
  deleted_at: string | null;
}

export interface AccountDetail {
  account: AccountRow & {
    parent_account_id: string | null;
    password_algo: string;
    password_updated_at: string;
    updated_at: string;
  };
  devices: Array<{
    id: string; label: string; platform: string;
    last_ip: string | null; last_seen_at: string;
    created_at: string; revoked_at: string | null;
  }>;
  servers: Array<{
    id: string; server_id: string; label: string;
    paired_at: string | null; last_seen_at: string | null;
    revoked_at: string | null;
  }>;
  recent_events: Array<{
    id: number; ts: string; event_type: string; ip: string | null;
    detail: Record<string, unknown> | null;
  }>;
  active_sessions: number;
}

// Login flow
export interface LoginSuccess {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: "Bearer";
  account: { id: string; email: string; display_name: string; role: string };
  device: { id: string; label: string };
}
export interface Login2FAChallenge {
  requires_2fa: true;
  temp_token: string;
  expires_in: number;
}
export type LoginResponse = LoginSuccess | Login2FAChallenge;

export async function login(api: Api, email: string, password: string) {
  return api.json<LoginResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password, device_label: "admin-ui", platform: "web" }),
  });
}
export async function login2FA(api: Api, tempToken: string, code: string) {
  return api.json<LoginSuccess>("/auth/login/2fa", {
    method: "POST",
    body: JSON.stringify({ temp_token: tempToken, code }),
  });
}
export async function refresh(api: Api, refreshToken: string) {
  return api.json<LoginSuccess>("/auth/refresh", {
    method: "POST",
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
}

// Admin endpoints
export async function getStats(api: Api) {
  return api.json<{ stats: AdminStats }>("/admin/stats");
}
export async function listAccounts(api: Api, q = "", limit = 50) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (q) qs.set("q", q);
  return api.json<{ accounts: AccountRow[] }>(`/admin/accounts?${qs}`);
}
export async function getAccount(api: Api, id: string) {
  return api.json<AccountDetail>(`/admin/accounts/${id}`);
}
export async function unlockAccount(api: Api, id: string) {
  return api.json<{ unlocked: true }>(`/admin/accounts/${id}/unlock`, { method: "POST" });
}
export async function forceLogout(api: Api, id: string) {
  return api.json<{ revoked: number; webhook: { attempted: number; delivered: number } }>(
    `/admin/accounts/${id}/force-logout`,
    { method: "POST" },
  );
}
export async function adminDisable2FA(api: Api, id: string) {
  return api.json<{ disabled: true }>(`/admin/accounts/${id}/disable-2fa`, { method: "POST" });
}
export async function searchEvents(api: Api, opts: { account_id?: string; type?: string; limit?: number } = {}) {
  const qs = new URLSearchParams();
  if (opts.account_id) qs.set("account_id", opts.account_id);
  if (opts.type) qs.set("type", opts.type);
  if (opts.limit) qs.set("limit", String(opts.limit));
  return api.json<{
    events: Array<{
      id: number; ts: string; account_id: string | null; device_id: string | null;
      event_type: string; ip: string | null; detail: Record<string, unknown> | null;
    }>;
  }>(`/admin/auth-events?${qs}`);
}
